/**
 * After a deploy the old chunk hashes are gone, so an open tab fails to load a lazy route
 * ("Failed to fetch dynamically imported module" / "Loading chunk N failed"). Reloading once
 * fetches the new index.html. A timestamp in sessionStorage stops a reload loop when the
 * chunk is really missing: within RELOAD_WINDOW_MS of a reload the error is reported instead.
 */
export const CHUNK_RELOAD_KEY = 'jft-chunk-reload';
const RELOAD_WINDOW_MS = 60_000;

const CHUNK_PATTERNS = [
    'Failed to fetch dynamically imported module',   // Chrome / Edge
    'error loading dynamically imported module',     // Firefox
    'Importing a module script failed',              // Safari
    'Loading chunk',                                 // webpack
    'ChunkLoadError',
];

function unwrap(err: any): any {
    let e = err;
    for (let i = 0; i < 3 && e && typeof e === 'object'; i++) {
        const inner = e.rejection ?? e.originalError ?? e.ngOriginalError;
        if (!inner || inner === e) {
            break;
        }
        e = inner;
    }
    return e;
}

export function isChunkLoadError(err: unknown): boolean {
    const e = unwrap(err);
    if (!e) {
        return false;
    }
    const text = typeof e === 'string' ? e : `${e.name ?? ''} ${e.message ?? ''}`;
    return CHUNK_PATTERNS.some(p => text.includes(p));
}

export interface ChunkReloadDeps {
    storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
    reload: () => void;
    now: () => number;
}

/** Returns true when it started a reload (the caller should then not report the error). */
export function reloadOnceOnChunkError(err: unknown, deps: ChunkReloadDeps): boolean {
    if (!isChunkLoadError(err)) {
        return false;
    }
    try {
        const last = Number(deps.storage.getItem(CHUNK_RELOAD_KEY) || 0);
        const now = deps.now();
        if (last && now - last < RELOAD_WINDOW_MS) {
            return false;
        }
        deps.storage.setItem(CHUNK_RELOAD_KEY, String(now));
    } catch {
        // No storage, no loop guard: report instead of risking endless reloads.
        return false;
    }
    deps.reload();
    return true;
}

export function browserChunkReloadDeps(): ChunkReloadDeps {
    return {
        // Accessed lazily: reading window.sessionStorage itself can throw (blocked site data).
        storage: {
            getItem: k => window.sessionStorage.getItem(k),
            setItem: (k, v) => window.sessionStorage.setItem(k, v),
            removeItem: k => window.sessionStorage.removeItem(k),
        },
        reload: () => window.location.reload(),
        now: () => Date.now(),
    };
}
